import { createContext, useCallback, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from "react"

export type ContentCapability = "pin" | "reset"

interface MountedContentStore {
  subscribe: (listener: () => void) => () => void
  get: (capability: ContentCapability) => string | null
  register: (capability: ContentCapability, dashboardId: string) => () => void
}

export function createMountedContentStore(): MountedContentStore {
  const mounted: Record<ContentCapability, string | null> = { pin: null, reset: null }
  const listeners = new Set<() => void>()
  const notify = () => listeners.forEach((listener) => listener())

  return {
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    get: (capability) => mounted[capability],
    register: (capability, dashboardId) => {
      mounted[capability] = dashboardId
      notify()
      return () => {
        if (mounted[capability] !== dashboardId) return
        mounted[capability] = null
        notify()
      }
    },
  }
}

const MountedContentContext = createContext<MountedContentStore | null>(null)

export function MountedContentProvider({ children }: { children: ReactNode }) {
  const [store] = useState(createMountedContentStore)
  return <MountedContentContext.Provider value={store}>{children}</MountedContentContext.Provider>
}

export function useRegisterMountedContent(capability: ContentCapability, dashboardId: string): void {
  const store = useContext(MountedContentContext)
  useEffect(() => store?.register(capability, dashboardId), [store, capability, dashboardId])
}

const subscribeToNothing = () => () => undefined

export function useMountedContentId(capability: ContentCapability, dashboardId: string | null): string | null {
  const store = useContext(MountedContentContext)
  const getSnapshot = useCallback(() => (store ? store.get(capability) : null), [store, capability])
  const mountedId = useSyncExternalStore(store?.subscribe ?? subscribeToNothing, getSnapshot)
  return mountedId !== null && mountedId === dashboardId ? mountedId : null
}
